import { apiGet } from './client';

/**
 * Phase 41 Plan 41-10 (Surface 1) — project template-type trio API.
 *
 * Wraps the 3 admin template-type list endpoints (Phase 35-04) and preserves
 * the cross-set companion fields (`recommended*Id`) so the trio recommendation
 * banner can derive the suggested pairing when one dropdown is changed.
 *
 * NAMING — why not `projectTemplates.ts`?
 * --------------------------------------
 * `services/api/projectTemplates.ts` already exists for the Phase 19 "project
 * template with states list" concept (the Re-apply template flow on
 * ProjectFormPage). That file owns `ProjectTemplate { id, name, states[] }`
 * and the `useProjectTemplates()` hook — overloading the same module with
 * the v7.0 template-type trio (PaymentType / ProjectType / TaskType) would
 * collide on hook name and confuse readers. This module sits next to
 * `services/api/templates.ts` (the unwrap-the-envelope variant added in
 * Phase 35) and exposes the wrapped `{ data: T[] }` shape that Phase 41
 * Plan 41-10's trio selector + recommendation banner consume directly via
 * TanStack Query (`hook.data?.data ?? []`).
 *
 * Per Phase 41-10 plan, exports `fetchPaymentTypes / fetchProjectTypes /
 * fetchTaskTypes` returning the wrapped envelope so the recommendation
 * banner test (`tests/components/surface-1-trio-banner.test.tsx`) and the
 * `useTemplateTypes` hooks consume `.data?.data` symmetrically.
 */

export interface PaymentType {
  id: number;
  name: string;
  isActive: boolean;
  recommendedProjectTypeId: number | null;
  recommendedTaskTypeId: number | null;
}

export interface ProjectType {
  id: number;
  name: string;
  isActive: boolean;
  color: string | null;
  recommendedPaymentTypeId: number | null;
  recommendedTaskTypeId: number | null;
}

export interface TaskType {
  id: number;
  name: string;
  isActive: boolean;
  recommendedPaymentTypeId: number | null;
  recommendedProjectTypeId: number | null;
}

export function fetchPaymentTypes() {
  return apiGet<{ data: PaymentType[] }>(`/admin/templates/payment-types`);
}

export function fetchProjectTypes() {
  return apiGet<{ data: ProjectType[] }>(`/admin/templates/project-types`);
}

export function fetchTaskTypes() {
  return apiGet<{ data: TaskType[] }>(`/admin/templates/task-types`);
}
